import { ComponentProps, useMemo } from "react";
import { Avatar, AvatarImage, AvatarFallback, type AvatarLike } from "@tellescope/ui";
import { formatInitials } from "@tellescope/utils";
import { useSecureFileUrl } from "@tellescope/tanstack-query";
import { useTranslation } from "react-i18next";

export type AvatarWithPictureProps<T extends AvatarLike> = {
  avatarLike: T;
  fallbackClassName?: string;
} & ComponentProps<typeof Avatar>;

export const AvatarWithPicture = <T extends AvatarLike>({
  avatarLike: { fname, lname, avatar, id },
  fallbackClassName,
  ...rest
}: AvatarWithPictureProps<T>) => {
  const { t } = useTranslation();

  // Fetch the avatar URL (handles both direct URLs and secure file names)
  const imageSrc = useSecureFileUrl(avatar);

  // Generate initials as fallback
  const initials = useMemo(() => formatInitials({ fname, lname }), [fname, lname]);

  // Use the user's ID for consistent avatar generation, fallback to name if no ID
  const avatarId = id || `${fname || ""}-${lname || ""}`.trim() || "anonymous";

  return (
    <Avatar {...rest}>
      {imageSrc && <AvatarImage src={imageSrc} alt={initials || t("Avatar")} />}
      <AvatarFallback
        className={fallbackClassName}
        useStaticBackground={true}
        deterministicColorMode="dark"
        id={avatarId}
      >
        {initials}
      </AvatarFallback>
    </Avatar>
  );
};
